import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ChangeEvent, ReactElement } from 'react'

import columns from './columns'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { DEFAULT_SEARCH, useTablePagination } from '~/hooks/useTablePagination'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppsListQuery } from '~/queries/applications'
import { useAssetsTagsKeysQuery } from '~/queries/assets'
import { useNavigationGraphsListQuery } from '~/queries/navigation-graphs'
import { useUsersListQuery } from '~/queries/users'
import { isUserId, formatBuilder } from '~/utils/builder'
import { debounce } from '~/utils/function'
import { copyText } from '~/utils/navigator'

const ENTRIES_NAME = 'navigation graphs'

const NavigationGraphsListing = (): ReactElement => {
  /** Local state */

  const navigationGraphWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.DYNAMIC_SCREENS.NAVIGATION_GRAPH.WRITE])

  const navigate = useNavigate()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  const paginationApi = useTablePagination(ENTRIES_NAME)

  const { filters, orderBy, page, search, setPage, setSearch, size } = paginationApi

  /** Queries & Mutations */

  const { data: usersData = [] } = useUsersListQuery()

  const { data: apps } = useAppsListQuery({})

  const { data: tags } = useAssetsTagsKeysQuery()

  const { data: navigationGraphs, isLoading: isNavigationGraphsLoading } = useNavigationGraphsListQuery({
    ['order-by']: orderBy,
    page,
    size,
    ...search,
    ...filters,
  })

  const rowData = navigationGraphs?.data?.map((navigationGraph) => ({
    ...navigationGraph,
    application: apps?.data?.find(
      (app) => app.app_key === navigationGraph.app_key && app.device_type === navigationGraph.device_type
    ),
    updated_at_ts: 1000000 * Date.parse(navigationGraph.updated_at),
    updated_by: isUserId(navigationGraph.updated_by)
      ? usersData.find((user) => user.user_id === navigationGraph.updated_by)
      : { name: formatBuilder(navigationGraph.updated_by) },
  }))

  /** Effects */

  useDocumentTitle('DS Navigation graphs')

  /** Handlers */

  const handleSearchChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value
    setSearch(text ? { id: text, title: text } : DEFAULT_SEARCH)
    setPage(0)
  }, 500)

  const handleRowClicked = (event: RowClickedEvent) => {
    if (gridRef.current?.api) {
      if (event.node.data.uuid && gridRef.current.api.getFocusedCell()?.column.getColId() !== 'actions') {
        if (isTouchDown) {
          window.open(`/dynamic-screens/navigation-graphs/${event.node.data.uuid}`, '_blank', 'rel=noopener noreferrer')
          setIsTouchDown(false)
        } else {
          navigate(`/dynamic-screens/navigation-graphs/${event.node.data.uuid}`)
        }

        gridRef.current.api.deselectAll()
      }
    }
  }

  const handleAddRowClicked = () => navigate('/dynamic-screens/navigation-graphs/creation')

  const handleCopyId = (id: string) => copyText(id, 'The Navigation graph ID has just been copied to the clipboard')

  /** Render */

  return (
    <Layout className="navigation-graphs-list-view" withPadding>
      <ViewTitle className="layout-view-title">Navigation graphs</ViewTitle>
      <ContentLayout>
        <Section className="section">
          <ServerSideTableWrapper
            addButton
            addButtonDisabled={!navigationGraphWriteAuthorized}
            addButtonLabel="New navigation graph"
            addButtonTooltip={!navigationGraphWriteAuthorized ? 'unauthorized' : undefined}
            className="navigation-graphs-table with-min-height"
            columnDefs={columns}
            context={{ handleCopyId, tags }}
            currentPage={page}
            entriesName={ENTRIES_NAME}
            loading={isNavigationGraphsLoading}
            onAddButtonClick={handleAddRowClicked}
            onRowClicked={handleRowClicked}
            onSearchChange={handleSearchChange}
            paginationApi={paginationApi}
            ref={gridRef}
            rowData={rowData || []}
            searchField="title"
            tableRef={tableRef}
            total={navigationGraphs?.total || 0}
            withSearchBar
          />
        </Section>
      </ContentLayout>
    </Layout>
  )
}

export default NavigationGraphsListing
